import React, { useState } from "react"
import dayjs from "dayjs"
import styled from "styled-components"
import { DownOutlined, RightOutlined } from "@ant-design/icons"
import { Tag, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHatWizard } from "@fortawesome/pro-regular-svg-icons"
import { IClub, IMeet } from "./utils"
import MeetClubStatus from "./MeetClubStatus"
import MagicResultsModal from "./MagicResultsModal"

export interface ISeasonMeetWithClubs {
  meet: IMeet & {
    latestPublishedAt?: string | null
    publishCount?: number
    multiSessionMeetId?: number | null
    sessionType?: "prelims" | "finals" | "timedFinals" | null
    sessionOrder?: number | null
    multiSessionMeetName?: string | null
  }
  meetCourse: "SCY" | "SCM" | "LCM"
  virtualMeetCourse: null | "SCY" | "SCM" | "LCM"
  isVirtualMeet: boolean
  startdate?: string
  clubs: {
    club: IClub
    meetstatus: null | 0 | 1 | 2
    entriesCount: number
    confirmLineup: boolean
    heatSheetAccepted: boolean
  }[]
  divisions?: {
    divisionId: number
    divisionNumber: number
    divisionName: string
    clubIds: number[]
  }[]
}

export type TSeasonMeetCardViewMode = "regular" | "compact"

const PARTIAL_LIST_SIZE = 4

/**
 * Sorts clubs into a stable order (host first, myClubs second, rest alphabetically)
 * and returns { sorted, visible, hidden }.
 * `visible` is always sorted.slice(0, PARTIAL_LIST_SIZE).
 * `hidden` is always sorted.slice(PARTIAL_LIST_SIZE).
 * Renders should always use `sorted` as the source of truth — never the original array —
 * so the order never changes when toggling between partial and full view.
 */
export function getPartialClubList<T extends { club: IClub }>(
  clubs: T[],
  hostClubId: number | null | undefined,
  myClubIds: number[],
): { sorted: T[]; visible: T[]; hidden: T[] } {
  const myClubIdSet = new Set(myClubIds)
  const priority: T[] = []
  const rest: T[] = []

  for (const c of clubs) {
    const id = c.club.clubId
    if (id === hostClubId || myClubIdSet.has(id ?? -1)) {
      priority.push(c)
    } else {
      rest.push(c)
    }
  }

  rest.sort((a, b) =>
    (a.club.nameLong || a.club.name || "").localeCompare(b.club.nameLong || b.club.name || ""),
  )

  const sorted = [...priority, ...rest]
  const visible = sorted.slice(0, PARTIAL_LIST_SIZE)
  const hidden = sorted.slice(PARTIAL_LIST_SIZE)

  return { sorted, visible, hidden }
}

export const SeasonMeetCardShell = styled.div`
  .season-shared-meet-card {
    min-width: 0;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 5px;
    padding: 16px 18px;
    box-shadow: 0 1px 2px 2px rgba(0, 0, 0, 0.04);
    transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, padding 180ms ease, border-radius 180ms ease;
    border-left-width: 4px;
    border-left-style: solid;
  }

  .season-shared-meet-card.clickable {
    cursor: pointer;
  }

  .season-shared-meet-card.clickable:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(16, 18, 66, 0.10);
  }

  .season-shared-meet-card.compact {
    padding: 12px 14px;
  }

  .season-shared-meet-card.status-editing-entries {
    border-left-color: #faad14;
    background: linear-gradient(180deg, rgba(255, 251, 230, 0.96) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-shared-meet-card.status-heat-sheet-merged {
    border-left-color: #1677ff;
    background: linear-gradient(180deg, rgba(240, 247, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-shared-meet-card.status-results-published {
    border-left-color: #52c41a;
    background: linear-gradient(180deg, rgba(246, 255, 237, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-shared-meet-card.virtual-leg {
    border-color: rgba(22, 119, 255, 0.16);
    background: linear-gradient(180deg, rgba(248, 251, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-shared-meet-row-header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .season-shared-meet-card.compact .season-shared-meet-row-header {
    margin-bottom: 8px;
  }

  .season-shared-meet-row-title {
    min-width: 0;
    flex: 1 1 420px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .season-shared-meet-row-title h3 {
    margin: 0;
    color: #101242;
    min-width: 0;
    font-size: 20px;
    line-height: 1.25;
  }

  .season-shared-meet-meta {
    margin-top: 0;
    font-size: 13px;
    line-height: 1.55;
    color: rgba(16, 18, 66, 0.72);
  }

  .season-shared-meet-card.compact .season-shared-meet-meta {
    font-size: 12px;
  }

  .season-shared-meet-status-stack {
    flex: 0 0 220px;
    min-width: 180px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
    gap: 4px;
  }

  .season-shared-status-summary {
    min-width: 180px;
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    text-align: right;
  }

  .season-shared-meet-card.compact .season-shared-status-summary {
    min-width: 0;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  }

  .season-shared-status-detail {
    font-size: 12px;
    line-height: 1.5;
    color: rgba(16, 18, 66, 0.62);
  }

  .magic-wizard-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 6px;
    color: #722ed1;
    font-size: 16px;
    line-height: 1;
    border-radius: 6px;
    transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
  }
  .magic-wizard-btn:hover {
    background: rgba(114, 46, 209, 0.1);
    color: #531dab;
    transform: rotate(-8deg) scale(1.15);
  }
  .magic-wizard-btn:active {
    transform: rotate(-8deg) scale(1.05);
  }

  .season-shared-status-publish-label {
    font-size: 11px;
    line-height: 1.4;
    color: rgba(16, 18, 66, 0.62);
  }

  .season-shared-compact-summary {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(16, 18, 66, 0.08);
    font-size: 12px;
    line-height: 1.5;
    color: rgba(16, 18, 66, 0.72);
    display: flex;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
  }

  .season-shared-compact-summary strong {
    color: #101242;
  }

  .season-shared-section-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #1677ff;
    cursor: pointer;
    user-select: none;
    background: none;
    border: none;
    padding: 0;
  }

  @media (max-width: 640px) {
    .season-shared-meet-row-title {
      flex-basis: 100%;
    }

    .season-shared-meet-card.hide-publish-label-mobile .season-shared-meet-row-header {
      gap: 8px;
    }

    .season-shared-meet-row-title h3 {
      font-size: 18px;
    }

    .season-shared-meet-meta {
      margin-top: 0;
    }

    .season-shared-meet-status-stack {
      width: 100%;
      min-width: 0;
      margin-left: 0;
      align-items: flex-start;
      text-align: left;
    }

    .season-shared-meet-card.hide-publish-label-mobile .season-shared-status-publish-label {
      display: none;
    }
  }
`

function getSeasonMeetStatusMeta(meetStatus: number | null | undefined) {
  if (meetStatus === 2) {
    return {
      key: "results-published",
      label: "Results Published",
      color: "success" as const,
      detail: null,
    }
  }

  if (meetStatus === 1) {
    return {
      key: "heat-sheet-merged",
      label: "Heat Sheet Merged",
      color: "processing" as const,
      detail: "This meet is merged and ready for race-day operations.",
    }
  }

  return {
    key: "editing-entries",
    label: "Editing Entries",
    color: "warning" as const,
    detail: "Entries are still being prepared for this meet.",
  }
}

export function getMeetPublishLabel({
  latestPublishedAt,
  publishCount,
}: {
  latestPublishedAt?: string | null
  publishCount?: number | null
}): string | null {
  if (!latestPublishedAt || !publishCount) {
    return null
  }

  const publishMoment = dayjs(latestPublishedAt)

  if (!publishMoment.isValid()) {
    return null
  }

  return `${publishCount > 1 ? "Republished" : "Published"} ${publishMoment.format(
    "MM-DD-YYYY h:mm A",
  )}`
}

export default function SeasonMeetCard({
  meet,
  viewMode,
  teamsExpanded,
  onToggleTeams,
  onOpenMeet,
  hidePublishLabelOnMobile,
  myClubId,
  isSwimminglyAdmin = false,
  seasonEnded = false,
  onRefresh,
}: {
  meet: ISeasonMeetWithClubs
  viewMode: TSeasonMeetCardViewMode
  teamsExpanded: boolean
  onToggleTeams?: () => void
  onOpenMeet?: () => void
  hidePublishLabelOnMobile?: boolean
  myClubId?: number | null
  isSwimminglyAdmin?: boolean
  seasonEnded?: boolean
  onRefresh?: () => void
}): JSX.Element {
  const [showAllClubs, setShowAllClubs] = useState(false)
  const [magicModalOpen, setMagicModalOpen] = useState(false)
  const statusMeta = getSeasonMeetStatusMeta(meet.meet.meetstatus)
  const publishLabel =
    viewMode === "regular" && meet.meet.meetstatus === 2
      ? getMeetPublishLabel({
          latestPublishedAt: meet.meet.latestPublishedAt,
          publishCount: meet.meet.publishCount,
        })
      : null
  const compactReadyClubCount = meet.clubs.filter((club) => {
    if (meet.meet.meetstatus === 1 || meet.meet.meetstatus === 2) {
      return true
    }

    return Boolean(club.heatSheetAccepted)
  }).length
  const compactSummary =
    meet.meet.meetstatus === 1 || meet.meet.meetstatus === 2
      ? `${meet.clubs.length} clubs listed`
      : `${compactReadyClubCount} of ${meet.clubs.length} clubs heat-sheet ready`

  return (
    <SeasonMeetCardShell>
      <div
        className={[
          "season-shared-meet-card",
          `status-${statusMeta.key}`,
          meet.isVirtualMeet ? "virtual-leg" : "",
          viewMode === "compact" ? "compact" : "",
          hidePublishLabelOnMobile ? "hide-publish-label-mobile" : "",
          onOpenMeet ? "clickable" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={onOpenMeet}
      >
        <div className="season-shared-meet-row-header">
          <div className="season-shared-meet-row-title">
            {meet.meet.sessionType ? (
              <div style={{ marginBottom: 2 }}>
                <Tag
                  color={
                    meet.meet.sessionType === "prelims"
                      ? "blue"
                      : meet.meet.sessionType === "finals"
                        ? "purple"
                        : "default"
                  }
                  style={{ marginInlineEnd: 0 }}
                >
                  {meet.meet.sessionType === "prelims"
                    ? "Prelims"
                    : meet.meet.sessionType === "finals"
                      ? "Finals"
                      : "Timed Finals"}
                </Tag>
              </div>
            ) : null}
            <h3>
              {meet.meet.meetName.length > 57
                ? `${meet.meet.meetName.slice(0, 54)}...`
                : meet.meet.meetName}
            </h3>
            <div className="season-shared-meet-meta">
              {meet.isVirtualMeet && meet.startdate ? (
                <Tooltip title="Run your swim meet anytime during or after the scheduled meet window.">
                  <span>
                    Meet Window: {dayjs(meet.startdate).format("MM-DD-YYYY")} to {dayjs(
                      meet.meet.meet_date,
                    ).format("MM-DD-YYYY")}
                  </span>
                </Tooltip>
              ) : (
                <span>{dayjs(meet.meet.meet_date).format("MM-DD-YYYY")}</span>
              )}
              <br />
              <span>Course: {meet.meetCourse}</span>
              {meet.virtualMeetCourse !== null ? (
                <span>
                  <br />
                  Virtual Course: {meet.virtualMeetCourse}
                </span>
              ) : null}
            </div>
          </div>
          {viewMode !== "compact" ? (
            <div className="season-shared-meet-status-stack">
              <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
              {publishLabel ? (
                <div className="season-shared-status-publish-label">{publishLabel}</div>
              ) : null}
              {statusMeta.detail ? (
                <div className="season-shared-status-detail">{statusMeta.detail}</div>
              ) : null}
              {isSwimminglyAdmin ? (
                <Tooltip title={seasonEnded ? "Test results cannot be generated for past seasons" : ""}>
                  <button
                    type="button"
                    aria-label="Generate test results"
                    className="magic-wizard-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      setMagicModalOpen(true)
                    }}
                  >
                    <FontAwesomeIcon icon={faHatWizard} />
                  </button>
                </Tooltip>
              ) : null}
            </div>
          ) : null}
          {viewMode === "compact" ? (
            <div className="season-shared-status-summary">
              <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
              <div className="season-shared-status-detail">{compactSummary}</div>
            </div>
          ) : null}
        </div>

        {viewMode === "compact" ? (
          <div className="season-shared-compact-summary">
            <span>
              <strong>
                {meet.meet.meetstatus === 1 || meet.meet.meetstatus === 2
                  ? "Club Status"
                  : "Heat Sheet-Ready Tracker"}
              </strong>{" "}
              {compactSummary}
            </span>
            <span>Tap to open the full meet workspace.</span>
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#595959",
                  letterSpacing: 0.2,
                  textTransform: "uppercase",
                }}
              >
                {meet.meet.meetstatus === 1 || meet.meet.meetstatus === 2
                  ? "Club Status"
                  : "Heat Sheet-Ready Tracker"}
              </div>
              {onToggleTeams ? (
                <button
                  type="button"
                  className="season-shared-section-toggle"
                  onClick={(event) => {
                    event.stopPropagation()
                    onToggleTeams()
                  }}
                >
                  {teamsExpanded ? <DownOutlined /> : <RightOutlined />}
                  <span>{teamsExpanded ? "Hide teams" : `Show teams (${meet.clubs.length})`}</span>
                </button>
              ) : null}
            </div>
            {teamsExpanded ? (
              (() => {
                const { sorted, visible, hidden } = getPartialClubList(
                  meet.clubs,
                  meet.meet.is_home,
                  myClubId != null ? [myClubId] : [],
                )
                const displayedClubs = showAllClubs ? sorted : visible
                const hasDivisions =
                  Array.isArray(meet.divisions) && meet.divisions.length > 0

                if (hasDivisions) {
                  // Build a clubId→divisionName map
                  const clubToDivision = new Map<number, string>()
                  for (const div of meet.divisions!) {
                    for (const clubId of div.clubIds) {
                      clubToDivision.set(clubId, div.divisionName)
                    }
                  }

                  // Group sorted clubs by division
                  const divisionGroups = new Map<string, typeof sorted>()
                  for (const div of meet.divisions!) {
                    divisionGroups.set(div.divisionName, [])
                  }
                  const unassigned: typeof sorted = []

                  for (const club of sorted) {
                    const divName = clubToDivision.get(club.club.clubId ?? -1)
                    if (divName && divisionGroups.has(divName)) {
                      divisionGroups.get(divName)!.push(club)
                    } else {
                      unassigned.push(club)
                    }
                  }

                  const allGroupedClubs: typeof sorted = []
                  Array.from(divisionGroups.values()).forEach((clubs) => allGroupedClubs.push(...clubs))
                  allGroupedClubs.push(...unassigned)
                  const totalHidden = Math.max(0, allGroupedClubs.length - PARTIAL_LIST_SIZE)
                  const displayLimit = showAllClubs ? Infinity : PARTIAL_LIST_SIZE

                  let renderedCount = 0
                  const groupNodes: React.ReactNode[] = []

                  const divisionEntries = Array.from(divisionGroups.entries())
                  if (unassigned.length > 0) {
                    divisionEntries.push(["Unassigned", unassigned])
                  }

                  for (const [divName, clubs] of divisionEntries) {
                    if (clubs.length === 0) continue
                    if (renderedCount >= displayLimit) break
                    groupNodes.push(
                      <div
                        key={`div_group_${divName}`}
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: divName === "Unassigned" ? "#bfbfbf" : "#595959",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          marginTop: renderedCount > 0 ? 6 : 2,
                          marginBottom: 2,
                        }}
                      >
                        {divName}
                      </div>,
                    )
                    for (const club of clubs) {
                      if (renderedCount >= displayLimit) break
                      groupNodes.push(
                        <MeetClubStatus
                          key={`meet_status_${meet.meet.meetId}_${club.club.clubId}`}
                          club={club.club}
                          isHome={meet.meet.is_home === club.club.clubId}
                          meetstatus={club.meetstatus}
                          overallMeetStatus={meet.meet.meetstatus}
                          entriesCount={club.entriesCount}
                          confirmLineup={club.confirmLineup}
                          heatSheetAccepted={club.heatSheetAccepted}
                        />,
                      )
                      renderedCount++
                    }
                  }

                  return (
                    <>
                      {groupNodes}
                      {totalHidden > 0 && (
                        <button
                          type="button"
                          className="season-shared-section-toggle"
                          style={{ marginTop: 4, display: "block" }}
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowAllClubs((v) => !v)
                          }}
                        >
                          {showAllClubs ? (
                            <><DownOutlined style={{ fontSize: 10 }} /> Show fewer teams</>
                          ) : (
                            <><RightOutlined style={{ fontSize: 10 }} /> Show all {meet.clubs.length} teams</>
                          )}
                        </button>
                      )}
                    </>
                  )
                }

                return (
                  <>
                    {displayedClubs.map((club, index) => (
                      <MeetClubStatus
                        key={`meet_status_${meet.meet.meetId}_${club.club.clubId}_${index}`}
                        club={club.club}
                        isHome={meet.meet.is_home === club.club.clubId}
                        meetstatus={club.meetstatus}
                        overallMeetStatus={meet.meet.meetstatus}
                        entriesCount={club.entriesCount}
                        confirmLineup={club.confirmLineup}
                        heatSheetAccepted={club.heatSheetAccepted}
                      />
                    ))}
                    {hidden.length > 0 && (
                      <button
                        type="button"
                        className="season-shared-section-toggle"
                        style={{ marginTop: 4, display: "block" }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowAllClubs((v) => !v)
                        }}
                      >
                        {showAllClubs ? (
                          <><DownOutlined style={{ fontSize: 10 }} /> Show fewer teams</>
                        ) : (
                          <><RightOutlined style={{ fontSize: 10 }} /> Show all {meet.clubs.length} teams</>
                        )}
                      </button>
                    )}
                  </>
                )
              })()
            ) : (
              <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.72)" }}>
                {compactSummary}. Expand to review each club.
              </div>
            )}
          </div>
        )}
      </div>
      {isSwimminglyAdmin ? (
        <MagicResultsModal
          meetId={meet.meet.meetId}
          meetName={meet.meet.meetName}
          resultSource={meet.meet.resultSource ?? null}
          meetstatus={meet.meet.meetstatus ?? null}
          seasonEnded={seasonEnded}
          open={magicModalOpen}
          onClose={() => setMagicModalOpen(false)}
          onSuccess={() => {
            setMagicModalOpen(false)
            onRefresh?.()
          }}
        />
      ) : null}
    </SeasonMeetCardShell>
  )
}